import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import HTMLFlipBook from 'react-pageflip';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  ZoomIn,
  Volume2,
  VolumeX,
  Share2,
  MessageCircle,
  Link2,
  BookDown,
  Loader2,
} from 'lucide-react';
import { PAGES } from '../brochurePages';
import { playPageFlip } from '../pageFlipSound';
import { buildEpub } from '../ebookExport';
import { useLang } from '../i18n';
import PageZoomModal from './PageZoomModal';

const RATIO = 1125 / 2000;

const Page = forwardRef(({ page, index }, ref) => (
  <div ref={ref} className="page" data-testid={`book-page-${index + 1}`}>
    <div className="page-content">
      <img src={page.src} alt={page.alt} draggable={false} />
      <div className="page-spine-shadow" />
      {index > 0 && (
        <span className="page-number-tag">
          {index + 1} / {PAGES.length}
        </span>
      )}
    </div>
  </div>
));
Page.displayName = 'Page';

const useBookSize = (fullscreen) => {
  const compute = useCallback(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let h = Math.min(vh * (fullscreen ? 0.86 : 0.72), 940);
    let w = h * RATIO;
    const maxW = vw * 0.92;
    if (w * 2 > maxW) {
      w = maxW / 2;
      h = w / RATIO;
    }
    return { w: Math.round(w), h: Math.round(h) };
  }, [fullscreen]);

  const [size, setSize] = useState(compute);
  useEffect(() => {
    const onResize = () =>
      setSize((prev) => {
        const next = compute();
        const significant =
          Math.abs(next.w - prev.w) > 24 || Math.abs(next.h - prev.h) > 24;
        return significant ? next : prev;
      });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [compute]);
  return size;
};

const FlipBookViewer = () => {
  const { t } = useLang();
  const bookRef = useRef(null);
  const stageRef = useRef(null);
  const shareRef = useRef(null);
  const [current, setCurrent] = useState(0);
  const [zoomPage, setZoomPage] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [soundOn, setSoundOn] = useState(
    () => localStorage.getItem('tm-sound') !== 'off'
  );
  const soundOnRef = useRef(soundOn);
  const size = useBookSize(isFullscreen);

  useEffect(() => {
    soundOnRef.current = soundOn;
  }, [soundOn]);

  const toggleSound = () =>
    setSoundOn((s) => {
      localStorage.setItem('tm-sound', s ? 'off' : 'on');
      return !s;
    });

  const handleStateChange = useCallback((e) => {
    if (e.data === 'flipping' && soundOnRef.current) playPageFlip();
  }, []);

  useEffect(() => {
    if (!shareOpen) return;
    const close = (e) => {
      if (shareRef.current && !shareRef.current.contains(e.target))
        setShareOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [shareOpen]);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText =
    'Trinity Masterclass 2026-2027 — Sfoglia la brochure digitale di Amici per la Musica APS';

  const shareWhatsApp = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
      '_blank',
      'noopener'
    );
    setShareOpen(false);
  };

  const shareNative = async () => {
    try {
      await navigator.share({ title: 'Trinity Masterclass', text: shareText, url: shareUrl });
    } catch {
      /* annullato */
    }
    setShareOpen(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t('share.copied'));
    } catch {
      toast.error(t('share.copyError'));
    }
    setShareOpen(false);
  };

  const downloadEbook = async () => {
    setPdfBusy(true);
    try {
      const blob = await buildEpub();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'trinity-masterclass-2026-2027.epub';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(t('share.ebookDone'));
    } catch {
      toast.error(t('share.ebookError'));
    }
    setPdfBusy(false);
    setShareOpen(false);
  };

  const flip = useCallback((dir) => {
    const api = bookRef.current?.pageFlip();
    if (!api) return;
    if (dir > 0) api.flipNext();
    else api.flipPrev();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (zoomPage !== null) return;
      if (e.key === 'ArrowRight') flip(1);
      if (e.key === 'ArrowLeft') flip(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [flip, zoomPage]);

  useEffect(() => {
    const onFs = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else stageRef.current?.requestFullscreen?.();
  };

  const goTo = (i) => bookRef.current?.pageFlip()?.flip(i);

  return (
    <section
      id="sfoglia"
      data-testid="flipbook-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mb-14"
        >
          <p className="font-mono-alt text-[10px] sm:text-xs tracking-[0.35em] uppercase text-[#e6c687] mb-4">
            {t('book.overline')}
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#f3e5ab] tracking-tight">
            {t('book.title.a')}{' '}
            <span className="italic text-[#d4af37]">{t('book.title.b')}</span>
          </h2>
          <p className="mt-4 max-w-xl text-sm sm:text-base text-[#a3b1b6] leading-relaxed">
            {t('book.desc')}
          </p>
        </motion.div>

        <div
          ref={stageRef}
          data-testid="flipbook-stage"
          className={`flipbook-stage relative flex flex-col items-center ${
            isFullscreen ? 'bg-[#070e12] justify-center py-6' : ''
          }`}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, rotateX: 8 }}
            whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="book-glow rounded-sm"
            style={{ perspective: 2000 }}
          >
            <HTMLFlipBook
              key={`${size.w}x${size.h}`}
              ref={bookRef}
              width={size.w}
              height={size.h}
              size="fixed"
              minWidth={180}
              maxWidth={size.w}
              minHeight={260}
              maxHeight={size.h}
              showCover
              usePortrait={false}
              drawShadow
              maxShadowOpacity={0.55}
              flippingTime={900}
              mobileScrollSupport
              swipeDistance={28}
              showPageCorners
              disableFlipByClick={false}
              className="flipbook"
              onFlip={(e) => setCurrent(e.data)}
              onChangeState={handleStateChange}
              startPage={0}
              autoSize={false}
              clickEventForward
              useMouseEvents
              style={{}}
            >
              {PAGES.map((p, i) => (
                <Page key={p.src} page={p} index={i} />
              ))}
            </HTMLFlipBook>
          </motion.div>

          <div
            data-testid="flipbook-controls"
            className="mt-10 flex flex-wrap items-center justify-center gap-3"
          >
            <button
              data-testid="flipbook-prev-btn"
              onClick={() => flip(-1)}
              disabled={current === 0}
              className="w-12 h-12 rounded-full border border-[#d4af37]/35 bg-[#0c171c]/70 backdrop-blur flex items-center justify-center text-[#e6c687] transition-all duration-300 hover:bg-[#d4af37]/15 hover:border-[#d4af37]/60 disabled:opacity-25 disabled:pointer-events-none"
              aria-label={t('controls.prev')}
            >
              <ChevronLeft size={20} strokeWidth={1.6} />
            </button>

            <span
              data-testid="flipbook-counter"
              className="font-mono-alt text-[10px] tracking-[0.3em] uppercase text-[#e6c687] border border-[#d4af37]/25 rounded-full px-5 py-3 bg-[#0c171c]/70 backdrop-blur min-w-[130px] text-center"
            >
              {t('controls.page')} {current + 1} / {PAGES.length}
            </span>

            <button
              data-testid="flipbook-next-btn"
              onClick={() => flip(1)}
              disabled={current >= PAGES.length - 1}
              className="w-12 h-12 rounded-full border border-[#d4af37]/35 bg-[#0c171c]/70 backdrop-blur flex items-center justify-center text-[#e6c687] transition-all duration-300 hover:bg-[#d4af37]/15 hover:border-[#d4af37]/60 disabled:opacity-25 disabled:pointer-events-none"
              aria-label={t('controls.next')}
            >
              <ChevronRight size={20} strokeWidth={1.6} />
            </button>

            <span className="hidden sm:block w-px h-8 bg-[#d4af37]/20 mx-2" />

            <button
              data-testid="flipbook-zoom-open-btn"
              onClick={() => setZoomPage(current)}
              className="inline-flex items-center gap-2.5 h-12 rounded-full border border-[#d4af37]/35 bg-[#0c171c]/70 backdrop-blur px-6 font-mono-alt text-[10px] tracking-[0.25em] uppercase text-[#e6c687] transition-all duration-300 hover:bg-[#d4af37]/15 hover:border-[#d4af37]/60"
            >
              <ZoomIn size={16} strokeWidth={1.6} />
              {t('controls.zoom')}
            </button>

            <button
              data-testid="sound-toggle-btn"
              onClick={toggleSound}
              className={`w-12 h-12 rounded-full border backdrop-blur flex items-center justify-center transition-all duration-300 ${
                soundOn
                  ? 'border-[#d4af37]/60 bg-[#d4af37]/15 text-[#f3e5ab]'
                  : 'border-[#d4af37]/25 bg-[#0c171c]/70 text-[#a3b1b6]'
              } hover:border-[#d4af37]/60`}
              aria-label={soundOn ? t('controls.soundOn') : t('controls.soundOff')}
              aria-pressed={soundOn}
            >
              {soundOn ? (
                <Volume2 size={17} strokeWidth={1.6} />
              ) : (
                <VolumeX size={17} strokeWidth={1.6} />
              )}
            </button>

            <div className="relative" ref={shareRef}>
              <button
                data-testid="share-btn"
                onClick={() => setShareOpen((o) => !o)}
                aria-expanded={shareOpen}
                className="inline-flex items-center gap-2.5 h-12 rounded-full border border-[#d4af37]/35 bg-[#0c171c]/70 backdrop-blur px-6 font-mono-alt text-[10px] tracking-[0.25em] uppercase text-[#e6c687] transition-all duration-300 hover:bg-[#d4af37]/15 hover:border-[#d4af37]/60"
              >
                <Share2 size={16} strokeWidth={1.6} />
                {t('share.title')}
              </button>
              <AnimatePresence>
                {shareOpen && (
                  <motion.div
                    data-testid="share-menu"
                    initial={{ opacity: 0, y: 10, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.96 }}
                    transition={{ duration: 0.18, ease: 'easeOut' }}
                    className="absolute bottom-14 left-1/2 -translate-x-1/2 z-40 w-60 rounded-2xl border border-[#d4af37]/25 bg-[#0c171c]/95 backdrop-blur-xl p-2 shadow-[0_24px_60px_-16px_rgba(0,0,0,0.85)]"
                  >
                    <button
                      data-testid="share-whatsapp-btn"
                      onClick={shareWhatsApp}
                      className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-left text-sm text-[#f4f1ea] transition-colors duration-200 hover:bg-[#d4af37]/12"
                    >
                      <MessageCircle size={16} className="text-[#d4af37]" strokeWidth={1.7} />
                      WhatsApp
                    </button>
                    {typeof navigator !== 'undefined' && navigator.share && (
                      <button
                        data-testid="share-native-btn"
                        onClick={shareNative}
                        className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-left text-sm text-[#f4f1ea] transition-colors duration-200 hover:bg-[#d4af37]/12"
                      >
                        <Share2 size={16} className="text-[#d4af37]" strokeWidth={1.7} />
                        {t('share.native')}
                      </button>
                    )}
                    <button
                      data-testid="share-copy-link-btn"
                      onClick={copyLink}
                      className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-left text-sm text-[#f4f1ea] transition-colors duration-200 hover:bg-[#d4af37]/12"
                    >
                      <Link2 size={16} className="text-[#d4af37]" strokeWidth={1.7} />
                      {t('share.copy')}
                    </button>
                    <div className="hairline-gold my-1.5" />
                    <button
                      data-testid="share-download-ebook-btn"
                      onClick={downloadEbook}
                      disabled={pdfBusy}
                      className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-left text-sm text-[#f4f1ea] transition-colors duration-200 hover:bg-[#d4af37]/12 disabled:opacity-50"
                    >
                      {pdfBusy ? (
                        <Loader2 size={16} className="text-[#d4af37] animate-spin" strokeWidth={1.7} />
                      ) : (
                        <BookDown size={16} className="text-[#d4af37]" strokeWidth={1.7} />
                      )}
                      {pdfBusy ? t('share.ebookBusy') : t('share.ebook')}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button
              data-testid="flipbook-fullscreen-btn"
              onClick={toggleFullscreen}
              className="inline-flex items-center gap-2.5 h-12 rounded-full bg-[#d4af37] px-6 font-mono-alt text-[10px] tracking-[0.25em] uppercase text-[#070e12] font-medium transition-all duration-300 hover:bg-[#f3e5ab] hover:shadow-[0_0_36px_-8px_rgba(212,175,55,0.6)]"
            >
              {isFullscreen ? (
                <Minimize2 size={16} strokeWidth={1.8} />
              ) : (
                <Maximize2 size={16} strokeWidth={1.8} />
              )}
              {isFullscreen ? t('controls.reduce') : t('controls.fullscreen')}
            </button>
          </div>

          <div
            data-testid="flipbook-thumbnails"
            className="mt-8 flex items-center justify-center gap-3 flex-wrap"
          >
            {PAGES.map((p, i) => (
              <button
                key={p.src}
                data-testid={`page-thumbnail-${i + 1}`}
                onClick={() => goTo(i)}
                className={`relative rounded-sm overflow-hidden border transition-all duration-300 ${
                  current === i
                    ? 'border-[#d4af37] shadow-[0_0_18px_-4px_rgba(212,175,55,0.55)] -translate-y-1'
                    : 'border-[#d4af37]/20 opacity-55 hover:opacity-90 hover:-translate-y-0.5'
                }`}
                aria-label={`Vai a ${p.label}`}
              >
                <img
                  src={p.src}
                  alt={p.label}
                  className="w-11 h-[76px] sm:w-12 sm:h-[84px] object-cover"
                  draggable={false}
                />
              </button>
            ))}
          </div>
        </div>
      </div>

      <PageZoomModal
        pageIndex={zoomPage}
        onClose={() => setZoomPage(null)}
        onNavigate={(i) => setZoomPage(i)}
        container={isFullscreen && stageRef.current ? stageRef.current : null}
      />
    </section>
  );
};

export default FlipBookViewer;
